'use client';

import { useState } from "react";
import { useRouter } from "next/navigation";
import { ChevronRight } from "lucide-react";
import { PARCOURS_CONFIG, ParcoursId } from "@/lib/constants";
import Image from "next/image";
import { BLUR_DATA_URLS } from "@/lib/blur-data-urls";
import { HistoryResetProvider } from "@/components/layouts/HistoryResetProvider";
import { OptInModal } from "@/components/layouts/OptInModal";

const OPT_IN_STORAGE_KEY = 'victoria_opt_in_accepted';

export default function Home() {
  const router = useRouter();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUserType, setSelectedUserType] = useState<ParcoursId | null>(null);

  const handleUserTypeClick = (userTypeId: ParcoursId) => {
    // Vérifier si l'opt-in a déjà été accepté
    const hasAccepted = localStorage.getItem(OPT_IN_STORAGE_KEY) === 'true';

    if (hasAccepted) {
      // Redirection directe si déjà accepté
      router.push(`/chat?type=${userTypeId}`);
    } else {
      // Afficher la modale si pas encore accepté
      setSelectedUserType(userTypeId);
      setIsModalOpen(true);
    }
  };

  const handleOptInAccept = () => {
    // Sauvegarder le consentement
    localStorage.setItem(OPT_IN_STORAGE_KEY, 'true');
    setIsModalOpen(false);

    // Rediriger vers le chat
    if (selectedUserType) {
      router.push(`/chat?type=${selectedUserType}`);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedUserType(null);
  };

  return (
    <HistoryResetProvider enabled={true}>
      <div className="flex flex-1 flex-col xl:flex-row-reverse items-center justify-center w-full h-full">
        <div className="flex-1 flex items-center justify-center w-full">
          <div className="relative w-full max-w-xl h-[280px] md:h-[652px] flex items-center justify-center">
            <Image
              src="/assets/images/global-composition-gilet.webp"
              alt="Composition globale avec gilet"
              fill
              className="object-contain"
              loading="lazy"
              fetchPriority="high"
              placeholder="blur"
              blurDataURL={BLUR_DATA_URLS.global_composition_gilet}
            />
          </div>
        </div>

        <div className="flex flex-col items-center justify-center flex-1 gap-8 text-center max-w-4xl">
          <h1 className="text-xl md:text-2xl font-normal text-black max-w-xl text-center mx-auto">
            Santé, pouvoir d&apos;achat, sécurité, souveraineté… Découvrez{" "}
            <span className="font-bold">VictorIA</span>, l&apos;agent
            conversationnel de Veolia, qui vous guide pas à pas pour trouver de
            nouvelles solutions.
          </h1>

          <div className="flex gap-6 mt-8 flex-col md:flex-row justify-center items-stretch max-w-4xl">
            {Object.values(PARCOURS_CONFIG).map((userType, index) => (
              <div key={index} className="flex flex-col items-center gap-4 max-w-sm">
                <button
                  onClick={() => handleUserTypeClick(userType.id)}
                  className="bg-primary text-white h-auto min-h-18 rounded-full px-4 py-2 text-base font-medium shadow-lg hover:bg-white hover:text-primary hover:border hover:border-primary hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer"
                  aria-label={`Accéder au chat en tant que ${userType.label}`}
                  tabIndex={0}
                >
                  <p className="whitespace-normal break-words max-w-[220px]">{userType.label}</p>
                  <ChevronRight size={24} />
                </button>

                {/* Description */}
                <p className=" font-medium leading-relaxed text-center max-w-xs px-2">
                  {userType.subTitle}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modale Opt-In */}
      <OptInModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onAccept={handleOptInAccept}
      />
    </HistoryResetProvider>
  );
}
